import equal from "fast-deep-equal";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import {
  computeMaterialBalances,
  MATERIAL_BALANCE_TOLERANCE,
  mixStateToMaterialTotals,
} from "./conversions";
import * as layerEditorState from "./layerEditorState";
import { BasketLayerState, MixLayerState } from "./types";

type EditorMix = Pick<MixLayerState, "mixId" | "baskets">;
type MixAction<Operation extends (arguments_: never) => BasketLayerState[]> =
  Omit<Parameters<Operation>[0], "baskets"> & { mixId: number };

const materialTotalsByMix = (mixes: EditorMix[]) =>
  new Map(mixes.map((mix) => [mix.mixId, mixStateToMaterialTotals(mix)]));

export const useLayerEditorController = <T extends EditorMix>({
  initialState,
  resetVersion = 0,
}: {
  initialState: T[];
  resetVersion?: number;
}) => {
  const [mixStates, setMixStates] = useState(initialState);
  const [previousResetVersion, setPreviousResetVersion] =
    useState(resetVersion);
  const [previousInitialState, setPreviousInitialState] =
    useState(initialState);
  const [baselineTotalsByMix, setBaselineTotalsByMix] = useState(() =>
    materialTotalsByMix(initialState)
  );
  const editorId = useId();
  const nextLayerNumberRef = useRef(0);
  const newLayerId = useCallback(() => {
    nextLayerNumberRef.current += 1;
    return `${editorId}-layer-${nextLayerNumberRef.current.toString()}`;
  }, [editorId]);

  if (
    resetVersion !== previousResetVersion ||
    (initialState !== previousInitialState &&
      !equal(initialState, previousInitialState))
  ) {
    setPreviousInitialState(initialState);
    setPreviousResetVersion(resetVersion);
    setBaselineTotalsByMix((previous) => {
      const currentTotals = materialTotalsByMix(mixStates);
      return new Map(
        initialState.map((mix) => {
          const incomingTotals = mixStateToMaterialTotals(mix);
          // Server summaries regenerate layer IDs, so totals identify edit echoes.
          const baseline = previous.get(mix.mixId);
          return [
            mix.mixId,
            baseline && equal(currentTotals.get(mix.mixId), incomingTotals)
              ? baseline
              : incomingTotals,
          ];
        })
      );
    });
    setMixStates(initialState);
  }

  const currentTotalsByMix = useMemo(
    () => materialTotalsByMix(mixStates),
    [mixStates]
  );
  const balancesByMix = useMemo(
    () =>
      new Map(
        mixStates.map((mix) => [
          mix.mixId,
          computeMaterialBalances(
            currentTotalsByMix.get(mix.mixId) ?? {},
            baselineTotalsByMix.get(mix.mixId) ?? {},
            MATERIAL_BALANCE_TOLERANCE
          ),
        ])
      ),
    [mixStates, currentTotalsByMix, baselineTotalsByMix]
  );
  const editedMixIds = useMemo(
    () =>
      new Set(
        mixStates
          .filter(
            (mix) =>
              !equal(
                mix,
                initialState.find((original) => original.mixId === mix.mixId)
              )
          )
          .map((mix) => mix.mixId)
      ),
    [mixStates, initialState]
  );
  const changeMix = useCallback(
    (
      mixId: number,
      changeBaskets: (baskets: BasketLayerState[]) => BasketLayerState[]
    ) => {
      setMixStates((previous) =>
        previous.map((mix) =>
          mix.mixId === mixId
            ? { ...mix, baskets: changeBaskets(mix.baskets) }
            : mix
        )
      );
    },
    []
  );
  const updateLayer = useCallback(
    ({ mixId, ...update }: MixAction<typeof layerEditorState.updateLayer>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.updateLayer({ baskets, ...update })
      );
    },
    [changeMix]
  );
  const deleteLayer = useCallback(
    ({
      mixId,
      ...deletion
    }: MixAction<typeof layerEditorState.deleteLayer>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.deleteLayer({ baskets, ...deletion })
      );
    },
    [changeMix]
  );
  const addLayer = useCallback(
    ({
      mixId,
      basketNumber,
      materialName,
      weight,
    }: {
      mixId: number;
      basketNumber: number;
      materialName: string;
      weight: number;
    }) => {
      const layer = {
        id: newLayerId(),
        materialName,
        weight,
        unit: "mass" as const,
        isNew: true,
      };
      changeMix(mixId, (baskets) =>
        layerEditorState.addLayer({ baskets, basketNumber, layer })
      );
    },
    [changeMix, newLayerId]
  );
  const splitLayer = useCallback(
    ({
      mixId,
      ...split
    }: Omit<
      MixAction<typeof layerEditorState.splitLayer>,
      "firstLayerId" | "secondLayerId"
    >) => {
      const firstLayerId = newLayerId();
      const secondLayerId = newLayerId();
      changeMix(mixId, (baskets) =>
        layerEditorState.splitLayer({
          baskets,
          ...split,
          firstLayerId,
          secondLayerId,
        })
      );
    },
    [changeMix, newLayerId]
  );
  const addBasket = useCallback(
    ({ mixId, ...addition }: MixAction<typeof layerEditorState.addBasket>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.addBasket({ baskets, ...addition })
      );
    },
    [changeMix]
  );
  const removeBasket = useCallback(
    ({
      mixId,
      ...removal
    }: MixAction<typeof layerEditorState.removeBasket>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.removeBasket({ baskets, ...removal })
      );
    },
    [changeMix]
  );
  const moveLayerByDrag = useCallback(
    ({
      mixId,
      ...movement
    }: MixAction<typeof layerEditorState.moveLayerByDrag>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.moveLayerByDrag({ baskets, ...movement })
      );
    },
    [changeMix]
  );
  const moveLayerVertically = useCallback(
    ({
      mixId,
      ...movement
    }: MixAction<typeof layerEditorState.moveLayerVertically>) => {
      changeMix(mixId, (baskets) =>
        layerEditorState.moveLayerVertically({ baskets, ...movement })
      );
    },
    [changeMix]
  );
  const reset = useCallback(() => {
    setMixStates(initialState);
    setBaselineTotalsByMix(materialTotalsByMix(initialState));
  }, [initialState]);

  return {
    mixStates,
    editedMixIds,
    baselineTotalsByMix,
    currentTotalsByMix,
    balancesByMix,
    updateLayer,
    deleteLayer,
    addLayer,
    splitLayer,
    addBasket,
    removeBasket,
    moveLayerByDrag,
    moveLayerVertically,
    reset,
  };
};
